import { Product } from '@wconcept/wconcept-common/models/display/product';
import { addAsIsMWebDomain } from '@wconcept/wconcept-common/utils/domainUtil';
import { useSearchParams } from 'next/navigation';
import qs from 'qs';
import { useCallback, useState } from 'react';
import { AggsOption } from '@wconcept/wconcept-common/models/display/search';
import { omit } from 'lodash-es';
import useGlobalState from './useGlobalState';
import useHistoryRouter from './useLinkTo';

interface ResetFilterOptions {
  preserveParams?: readonly string[];
}

function useFilterProductController({
  isCsrPage = false,
}: {
  isCsrPage?: boolean;
} = {}) {
  const { linkTo } = useHistoryRouter();
  const searchParams = useSearchParams();
  const { appInfo } = useGlobalState();

  const scrollToListTop = useCallback(() => {
    const $header =
      document.querySelector('#header') ||
      (document.querySelector('#headerDesktop .inner-nav') as HTMLDivElement);
    const headerHeight = $header?.clientHeight || 0;
    const gnbMenu = document.querySelector('#main-gnb-bar') as HTMLDivElement;
    const gnbMenuHeight = gnbMenu?.clientHeight || 0;
    const $fl = document.querySelector('.filter-listbar') as HTMLDivElement;
    const el = document.querySelector('.bottom-product-wrap') as HTMLDivElement;
    const elTop = el?.getBoundingClientRect().top || 0;
    const scrollY = elTop + window.pageYOffset - headerHeight - gnbMenuHeight;
    if (elTop < 0) {
      window.scrollTo(0, scrollY);
    }
  }, []);

  const changeRouterQuery = useCallback(
    (queryString: string) => {
      const trimmed = queryString.trim();

      // 1) full URL인지
      const isFullUrl = /^(https?:\/\/|\/\/|www\.)/.test(trimmed);

      let processedQueryString: string;
      if (isFullUrl) {
        processedQueryString = trimmed; // 그대로
      } else if (trimmed.startsWith('/')) {
        processedQueryString = trimmed; // 경로(+쿼리) 그대로
      } else if (trimmed.startsWith('?')) {
        processedQueryString = trimmed; // 쿼리 그대로
      } else {
        processedQueryString = `?${trimmed}`; // 쿼리로 간주
      }

      if (isCsrPage) {
        window.history.replaceState(null, '', processedQueryString);
      } else {
        linkTo.replace({
          url: processedQueryString,
          option: { scroll: false },
        });
      }
    },
    [isCsrPage, linkTo]
  );

  const handleFilterOptionChange = useCallback(
    (checked: boolean, code: string) => {
      const currentParams = qs.parse(searchParams.toString());
      const currentBenefits =
        (currentParams.benefit as string)?.split(',') || [];

      let newBenefits: string[];
      if (checked) {
        newBenefits = currentBenefits.includes(code)
          ? currentBenefits
          : [...currentBenefits, code];
      } else {
        newBenefits = currentBenefits.filter(benefit => benefit !== code);
      }

      const newParams = {
        benefit: newBenefits.length > 0 ? newBenefits.join(',') : '',
      };
      const mergedParams = omit({ ...currentParams, ...newParams }, ['page']);
      const queryString = qs.stringify(mergedParams);
      changeRouterQuery(queryString);
      scrollToListTop();
    },
    [changeRouterQuery, scrollToListTop, searchParams]
  );

  const handleSoldoutChange = useCallback(
    (checked: boolean) => {
      const currentParams = qs.parse(searchParams.toString());
      const newParams = { status: !checked ? '00' : '01' };
      const mergedParams = omit({ ...currentParams, ...newParams }, [
        'page',
        'previewDate',
      ]);
      const queryString = qs.stringify(mergedParams);
      changeRouterQuery(queryString);
      scrollToListTop();
    },
    [changeRouterQuery, scrollToListTop, searchParams]
  );

  const handleSortChange = useCallback(
    (sortParam: { code: string; name: string }) => {
      const currentParams = qs.parse(searchParams.toString());
      const newParams = { sort: sortParam.code };
      const mergedParams = omit({ ...currentParams, ...newParams }, [
        'page',
        'previewDate',
      ]);
      const queryString = qs.stringify(mergedParams);

      changeRouterQuery(queryString);
      scrollToListTop();
    },
    [changeRouterQuery, scrollToListTop, searchParams]
  );

  const handlePageChange = useCallback(
    (value: number) => {
      const currentParams = qs.parse(searchParams.toString());
      const newParams = { page: value };
      const mergedParams = { ...currentParams, ...newParams };
      const queryString = qs.stringify(mergedParams);
      changeRouterQuery(queryString);
      scrollToListTop();
    },
    [changeRouterQuery, scrollToListTop, searchParams]
  );

  const handleProductClick = useCallback(
    async (product: Product) => {
      const currentParams = qs.parse(searchParams.toString());
      const mergedParams = {
        ...currentParams,
        ...{
          entry_channel: 'brand',
          brand_no: product?.brandCd,
          brand_nm: product?.brandNameEn,
          brand_menu: 'LP',
        },
      };
      const domain = await addAsIsMWebDomain(
        product?.webViewUrl || product?.landingUrl || ''
      );
      const queryString = qs.stringify(mergedParams);
      linkTo.push({
        url: `${domain}?${queryString}`,
      });
    },
    [searchParams, linkTo]
  );

  const handelDcRateGroupChange = useCallback(
    async (item: AggsOption) => {
      const currentParams = qs.parse(searchParams.toString());
      const newParams = { dcRateGroup: item.code };
      const mergedParams = omit({ ...currentParams, ...newParams }, [
        'page',
        'saleTags',
        'category',
        'color',
        'price',
        'benefit',
        'discount',
        'brand',
        'newBrand',
        'topBrand',
      ]);
      const queryString = qs.stringify(mergedParams);
      changeRouterQuery(queryString);
      scrollToListTop();
    },
    [changeRouterQuery, scrollToListTop, searchParams]
  );

  const handleFilterSubmit = useCallback(
    (queryParam: string) => {
      changeRouterQuery(queryParam);
      scrollToListTop();
    },
    [changeRouterQuery, scrollToListTop]
  );

  const handleResetFilter = useCallback(
    (options?: ResetFilterOptions) => {
      const queryParams = qs.parse(window.location.search, {
        ignoreQueryPrefix: true,
      });

      const newQueryParams = {
        gnbType: queryParams.gnbType && queryParams.gnbType,
        status: queryParams.status && queryParams.status,
        sort: queryParams.sort && queryParams.sort,
        keyword: queryParams.keyword && queryParams.keyword,
        ...(options?.preserveParams ?? []).reduce<Record<string, unknown>>(
          (params, key) => ({
            ...params,
            [key]: queryParams[key] && queryParams[key],
          }),
          {}
        ),
      };

      const newQueryString = qs.stringify(newQueryParams);

      const newUrl = `${window.location.pathname}?${newQueryString}`;
      if (isCsrPage) {
        changeRouterQuery(newQueryString);
      } else {
        changeRouterQuery(newUrl);
      }
      scrollToListTop();
    },
    [changeRouterQuery, scrollToListTop, isCsrPage]
  );

  return {
    handleSortChange,
    handleSoldoutChange,
    handleFilterOptionChange,
    handlePageChange,
    handleProductClick,
    handelDcRateGroupChange,
    handleResetFilter,
    handleFilterSubmit,
  };
}

export default useFilterProductController;
